/**
 * withContextShape — generic IDataService decorator.
 *
 * Wraps any inner IDataService with a pure context-transform so its
 * getContext() output is reshaped before the form library sees it.
 * subscribe() and getState() pass straight through.
 *
 * This is the seam that keeps the form library indifferent to the
 * data source: DDS-PLS is already in the canonical shape and needs no
 * decorator, while a non-native source (FIFA today, anything later)
 * is composed as `withContextShape(sourceContext, sourceToCanonical)`.
 *
 * SOLID:
 *   - S: this module only adapts shape; it knows nothing about FIFA,
 *        DDS, or any transport.
 *   - O: a new source needs a new raw adapter + a new pure mapper —
 *        no change here.
 *   - L: the returned object conforms to IDataService, exactly like
 *        the service it wraps.
 *   - D: both the inner service and the transform are injected.
 */
export function withContextShape(innerService, mapContext) {
  if (!innerService) throw new Error("withContextShape: `innerService` is required");
  if (typeof mapContext !== "function") {
    throw new Error("withContextShape: `mapContext` must be a function");
  }

  return {
    /** @returns {Promise<object>} */
    async getContext() {
      const raw = await innerService.getContext();
      return mapContext(raw);
    },

    /**
     * @param {() => void} listener
     * @returns {() => void} unsubscribe
     */
    subscribe(listener) {
      return typeof innerService.subscribe === "function"
        ? innerService.subscribe(listener)
        : () => {};
    },

    /** Diagnostics passthrough. */
    getState() {
      return typeof innerService.getState === "function"
        ? innerService.getState()
        : {};
    },
  };
}
